import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Plus, Trash2, Pencil, FlaskConical, X, Lock, Inbox } from 'lucide-react';
import styles from './AlertsPage.module.css';
import * as alertsApi from '../../api/alertsApi';
import { useBilling } from '../../context/BillingContext';

const splitList = (s: string): string[] =>
  s.split(',').map(x => x.trim()).filter(Boolean);

const joinList = (xs?: string[] | null): string => (xs && xs.length ? xs.join(', ') : '');

interface FormState {
  id?: string;
  name: string;
  description: string;
  semantic_query: string;
  jurisdictions: string;
  keywords: string;
  status: string;
  regulation_types: string;
  ai_relevance_min: string;
  semantic_threshold: number;
  trigger_on_new: boolean;
}

const EMPTY_FORM: FormState = {
  name: '', description: '', semantic_query: '',
  jurisdictions: '', keywords: '', status: '', regulation_types: '',
  ai_relevance_min: '', semantic_threshold: 0.65, trigger_on_new: true,
};

function toForm(a: alertsApi.Alert): FormState {
  return {
    id: a.id,
    name: a.name,
    description: a.description ?? '',
    semantic_query: a.semantic_query ?? '',
    jurisdictions: joinList(a.filter_criteria?.jurisdictions),
    keywords: joinList(a.filter_criteria?.keywords),
    status: joinList(a.filter_criteria?.status),
    regulation_types: joinList(a.filter_criteria?.regulation_types),
    ai_relevance_min: a.filter_criteria?.ai_relevance_min != null ? String(a.filter_criteria.ai_relevance_min) : '',
    semantic_threshold: a.semantic_threshold ?? 0.65,
    trigger_on_new: a.trigger_on_new,
  };
}

function toPayload(f: FormState): alertsApi.AlertCreate {
  const ai = f.ai_relevance_min.trim();
  return {
    name: f.name.trim(),
    description: f.description.trim() || null,
    semantic_query: f.semantic_query.trim() || null,
    filter_criteria: {
      jurisdictions: splitList(f.jurisdictions),
      keywords: splitList(f.keywords),
      status: splitList(f.status),
      regulation_types: splitList(f.regulation_types),
      categories: [],
      ai_relevance_min: ai ? Math.max(0, Math.min(1, Number(ai))) : null,
    },
    semantic_threshold: f.semantic_threshold,
    trigger_on_new: f.trigger_on_new,
  };
}

function filterSummary(a: alertsApi.Alert): string {
  const fc = a.filter_criteria || ({} as alertsApi.AlertFilterCriteria);
  const parts: string[] = [];
  if (fc.jurisdictions?.length) parts.push(`Jurisdictions: ${fc.jurisdictions.join(', ')}`);
  if (fc.keywords?.length) parts.push(`Keywords: ${fc.keywords.join(', ')}`);
  if (fc.status?.length) parts.push(`Status: ${fc.status.join(', ')}`);
  if (fc.regulation_types?.length) parts.push(`Types: ${fc.regulation_types.join(', ')}`);
  if (fc.ai_relevance_min != null) parts.push(`AI relevance ≥ ${fc.ai_relevance_min}`);
  if (a.semantic_query) parts.push(`Semantic: “${a.semantic_query}”`);
  return parts.length ? parts.join(' · ') : 'Matches all new regulations';
}

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');

export const AlertsPage: React.FC = () => {
  const navigate = useNavigate();
  const { canUse } = useBilling();
  // Custom alerts are a paid entitlement (free tier = 0). Admins have no
  // subscription, so exempt them; trial/paid users pass canUse('custom_alerts').
  const isAdmin = (() => {
    try { return !!JSON.parse(localStorage.getItem('auth_meta') || '{}').is_admin; } catch { return false; }
  })();
  const canCreateAlerts = isAdmin || canUse('custom_alerts');
  const [alerts, setAlerts] = useState<alertsApi.Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ id: string; result: alertsApi.AlertTestResult } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // SRS #166: expected-volume preview, per-alert quality, recent matches with dismiss
  const [preview, setPreview] = useState<alertsApi.AlertPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [quality, setQuality] = useState<Record<string, alertsApi.AlertQuality>>({});
  const [matches, setMatches] = useState<{ id: string; rows: alertsApi.AlertTrigger[] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAlerts(await alertsApi.listAlerts());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form || !form.name.trim()) { setError('Alert name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (form.id) await alertsApi.updateAlert(form.id, toPayload(form));
      else await alertsApi.createAlert(toPayload(form));
      setForm(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save alert');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a: alertsApi.Alert) => {
    setBusyId(a.id);
    try {
      await alertsApi.updateAlert(a.id, { is_active: !a.is_active });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update alert');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (a: alertsApi.Alert) => {
    if (!window.confirm(`Delete alert “${a.name}”?`)) return;
    setBusyId(a.id);
    try {
      await alertsApi.deleteAlert(a.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete alert');
    } finally {
      setBusyId(null);
    }
  };

  const runPreview = async () => {
    if (!form) return;
    setPreviewing(true); setError(null);
    try { setPreview(await alertsApi.previewAlert(toPayload(form))); }
    catch (e) { setError(e instanceof Error ? e.message : 'Preview failed'); }
    finally { setPreviewing(false); }
  };
  const loadQuality = async (a: alertsApi.Alert) => {
    try { const q = await alertsApi.alertQuality(a.id); setQuality(prev => ({ ...prev, [a.id]: q })); } catch { /* optional */ }
  };
  const showMatches = async (a: alertsApi.Alert) => {
    try { setMatches({ id: a.id, rows: await alertsApi.listAlertTriggers(a.id) }); loadQuality(a); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load matches'); }
  };
  const dismissOne = async (a: alertsApi.Alert, t: alertsApi.AlertTrigger) => {
    await alertsApi.dismissTrigger(a.id, t.id).catch(() => {});
    setMatches(m => (m && m.id === a.id ? { id: a.id, rows: m.rows.filter(r => r.id !== t.id) } : m));
    loadQuality(a);
  };
  const snooze = async (a: alertsApi.Alert) => {
    setBusyId(a.id);
    try {
      const updated = a.is_snoozed_until && new Date(a.is_snoozed_until) > new Date() ? await alertsApi.unsnoozeAlert(a.id) : await alertsApi.snoozeAlert(a.id, 7);
      setAlerts(prev => prev.map(x => (x.id === a.id ? updated : x)));
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed'); } finally { setBusyId(null); }
  };
  const fmtLatency = (s: number | null | undefined) => (s == null ? '—' : s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);
  const verdictColor: Record<string, string> = { too_narrow: 'var(--ds-danger)', quiet: 'var(--ds-text-muted)', good: 'var(--ds-success)', busy: 'var(--ds-warning)', too_broad: 'var(--ds-danger)' };

  const runTest = async (a: alertsApi.Alert) => {
    setBusyId(a.id);
    setTestResult(null);
    try {
      const result = await alertsApi.testAlert(a.id);
      setTestResult({ id: a.id, result });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to test alert');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleWrap}>
          <span className={styles.titleIcon}><Bell size={20} aria-hidden /></span>
          <div>
            <h1 className={styles.title}>Smart Alerts</h1>
            <p className={styles.subtitle}>Get notified when new regulations match your criteria.</p>
          </div>
        </div>
        <button
          className={styles.primaryBtn}
          title={canCreateAlerts ? 'Create a new alert' : 'Custom alerts are a Professional feature — upgrade to unlock'}
          onClick={() => {
            if (!canCreateAlerts) { navigate('/billing/checkout?plan=professional'); return; }
            setForm({ ...EMPTY_FORM }); setTestResult(null);
          }}
        >
          {canCreateAlerts ? <Plus size={16} aria-hidden /> : <Lock size={16} aria-hidden />}
          {canCreateAlerts ? ' New alert' : ' New alert (Pro)'}
        </button>
      </header>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {form && (
        <div className={styles.formCard}>
          <div className={styles.formHeader}>
            <h2>{form.id ? 'Edit alert' : 'New alert'}</h2>
            <button className={styles.iconBtn} onClick={() => setForm(null)} aria-label="Close"><X size={18} /></button>
          </div>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>Name *</span>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Facial recognition rules in the EU" />
            </label>
            <label className={styles.field}>
              <span>Description</span>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </label>
            <label className={`${styles.field} ${styles.full}`}>
              <span>Semantic query <em>(optional — natural language; uses AI matching)</em></span>
              <input value={form.semantic_query} onChange={e => setForm({ ...form, semantic_query: e.target.value })}
                     placeholder="Regulations affecting facial recognition in employment" />
            </label>
            <label className={styles.field}>
              <span>Jurisdictions <em>(comma-separated codes)</em></span>
              <input value={form.jurisdictions} onChange={e => setForm({ ...form, jurisdictions: e.target.value })}
                     placeholder="EU, US-FED, CN" />
            </label>
            <label className={styles.field}>
              <span>Keywords <em>(comma-separated)</em></span>
              <input value={form.keywords} onChange={e => setForm({ ...form, keywords: e.target.value })}
                     placeholder="facial recognition, biometric" />
            </label>
            <label className={styles.field}>
              <span>Status <em>(comma-separated)</em></span>
              <input value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                     placeholder="introduced, passed" />
            </label>
            <label className={styles.field}>
              <span>Regulation types <em>(comma-separated)</em></span>
              <input value={form.regulation_types} onChange={e => setForm({ ...form, regulation_types: e.target.value })}
                     placeholder="act, regulation" />
            </label>
            <label className={styles.field}>
              <span>Min AI relevance <em>(0–1)</em></span>
              <input type="number" min={0} max={1} step={0.05} value={form.ai_relevance_min}
                     onChange={e => setForm({ ...form, ai_relevance_min: e.target.value })} placeholder="0.5" />
            </label>
            <label className={styles.field}>
              <span>Semantic threshold: {form.semantic_threshold.toFixed(2)}</span>
              <input type="range" min={0} max={1} step={0.05} value={form.semantic_threshold}
                     onChange={e => setForm({ ...form, semantic_threshold: Number(e.target.value) })} />
            </label>
          </div>
          {preview && (
            <div className={styles.testBox} style={{ borderLeft: `4px solid ${verdictColor[preview.verdict] || 'var(--ds-text-muted)'}` }}>
              <strong>Expected volume:</strong> about {preview.per_week}/week ({preview.matched} of {preview.evaluated} regulations in the last {preview.lookback_days} days) —{' '}
              <span style={{ color: verdictColor[preview.verdict] }}>{preview.verdict.replace('_', ' ')}</span>. {preview.advice}
              {preview.samples.length > 0 && (
                <ul>{preview.samples.slice(0, 5).map(m => <li key={m.regulation_id}>{m.title} <em>({m.jurisdiction})</em> — {m.match_reason}</li>)}</ul>
              )}
            </div>
          )}
          <div className={styles.formActions}>
            <button className={styles.secondaryBtn} onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            <button className={styles.secondaryBtn} onClick={runPreview} disabled={saving || previewing} title="See how many matches these criteria would have produced recently">
              {previewing ? 'Checking…' : 'Preview volume'}
            </button>
            <button className={styles.primaryBtn} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : form.id ? 'Save changes' : 'Create alert'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className={styles.empty}>Loading alerts…</div>
      ) : alerts.length === 0 && !form ? (
        <div className={styles.empty}>
          <Bell size={28} aria-hidden />
          <p>No alerts yet. Create one to get notified when matching regulations are ingested.</p>
        </div>
      ) : (
        <ul className={styles.list}>
          {alerts.map(a => (
            <li key={a.id} className={styles.card} data-inactive={!a.is_active}>
              <div className={styles.cardMain}>
                <div className={styles.cardTop}>
                  <span className={styles.cardName}>{a.name}</span>
                  <span className={styles.badge} data-on={a.is_active}>{a.is_active ? 'Active' : 'Paused'}</span>
                </div>
                {a.description && <p className={styles.cardDesc}>{a.description}</p>}
                <p className={styles.cardCriteria}>{filterSummary(a)}</p>
                <p className={styles.cardMeta}>
                  Triggered {a.trigger_count}× · last {fmtDate(a.last_triggered_at)} · created {fmtDate(a.created_at)}
                </p>
                {a.is_snoozed_until && new Date(a.is_snoozed_until) > new Date() && (
                  <p className={styles.cardMeta} style={{ color: 'var(--ds-warning-text)' }}>Snoozed until {fmtDate(a.is_snoozed_until)}</p>
                )}
                {quality[a.id] && (
                  <p className={styles.cardMeta}>
                    Last 90 days: {quality[a.id].matches_90d} matches (~{quality[a.id].per_week}/week), {Math.round((quality[a.id].dismiss_rate || 0) * 100)}% dismissed ·
                    delivered {quality[a.id].latency_p50_seconds != null ? `${fmtLatency(quality[a.id].latency_p50_seconds)} after ingestion (median)` : 'latency not yet measured'}
                    {quality[a.id].within_15_min_pct != null && `, ${quality[a.id].within_15_min_pct}% within 15 min`}
                    {quality[a.id].verdict !== 'good' && <> · <span style={{ color: verdictColor[quality[a.id].verdict] }}>{quality[a.id].advice}</span></>}
                  </p>
                )}
                {matches?.id === a.id && (
                  <div className={styles.testBox}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>Recent matches ({matches.rows.length})</strong>
                      {matches.rows.length > 0 && <button className={styles.secondaryBtn} style={{ padding: '2px 8px', fontSize: 12 }} onClick={async () => { await alertsApi.dismissAllTriggers(a.id).catch(() => {}); setMatches({ id: a.id, rows: [] }); loadQuality(a); }}>Dismiss all</button>}
                    </div>
                    {matches.rows.length === 0 && <p className={styles.cardMeta}>No open matches.</p>}
                    <ul>
                      {matches.rows.map(t => (
                        <li key={t.id}>
                          {t.regulation_url ? <a href={t.regulation_url} target="_blank" rel="noreferrer">{t.regulation_title || t.regulation_id}</a> : (t.regulation_title || t.regulation_id)}
                          <em> ({t.jurisdiction_name})</em> — {t.explanation || t.match_reason}
                          {t.latency_seconds != null && <span style={{ color: 'var(--ds-text-faint)' }}> · {fmtLatency(t.latency_seconds)} after ingestion</span>}
                          <button className={styles.secondaryBtn} style={{ marginLeft: 6, padding: '1px 6px', fontSize: 11 }} onClick={() => dismissOne(a, t)}>dismiss</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {testResult?.id === a.id && (
                  <div className={styles.testBox}>
                    <strong>Dry-run:</strong> {testResult.result.matched} of {testResult.result.evaluated} recent regulations match.
                    <ul>
                      {testResult.result.matches.slice(0, 5).map(m => (
                        <li key={m.regulation_id}>{m.title} <em>({m.jurisdiction})</em> — {m.match_reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className={styles.cardActions}>
                <button className={styles.iconBtn} title="Test (dry-run)" disabled={busyId === a.id} onClick={() => runTest(a)}>
                  <FlaskConical size={16} />
                </button>
                <button className={styles.iconBtn} title="Recent matches & quality" onClick={() => (matches?.id === a.id ? setMatches(null) : showMatches(a))}>
                  <Inbox size={16} />
                </button>
                <button className={styles.iconBtn} title={a.is_snoozed_until && new Date(a.is_snoozed_until) > new Date() ? 'Unsnooze' : 'Snooze for 7 days'} disabled={busyId === a.id} onClick={() => snooze(a)}>
                  <BellOff size={16} />
                </button>
                <button className={styles.iconBtn} title="Edit" onClick={() => { setForm(toForm(a)); setTestResult(null); setPreview(null); }}>
                  <Pencil size={16} />
                </button>
                <button className={styles.iconBtn} title={a.is_active ? 'Pause' : 'Activate'} disabled={busyId === a.id}
                        onClick={() => toggleActive(a)}>
                  <Bell size={16} style={{ opacity: a.is_active ? 1 : 0.4 }} />
                </button>
                <button className={styles.iconBtn} title="Delete" disabled={busyId === a.id} onClick={() => remove(a)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AlertsPage;
